import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles';
import { faDownload } from '@fortawesome/pro-light-svg-icons/faDownload';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../../common/types';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLButton } from '../../components/primitives/SMLButton';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { releaseThemeMessages } from '../themes/releaseThemeMessages';
import { daysUntilRelease } from './releaseOrder';
import { formatReleaseWhen } from './releaseWhen';
import { releaseMessages } from './releaseMessages';
import { ReleaseArtwork } from './ReleaseArtwork';
import { useSmartLinkAddress } from './useSmartLinkAddress';
import { ReleasePresavePlanNote, showsPresavePlanNote } from './ReleasePresavePlanNote';
import { KitZip } from './assets/useKitZip';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './ReleasePage.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleaseHeaderProps {
  accountId: number;
  item: CatalogItem;
  /** The theme of the song now; null for a song with no kit (Clean). */
  themeId: ReleaseThemeId | null;
  /** True when one asset is ready: "Download all (.zip)" shows only then (design c3). */
  hasReadyAsset: boolean;
  zip: KitZip;
  onOpenStudio: () => void;
}

/**
 * The head of the release page (design b1, b2, b3): the cover, the type, the artist, the release
 * day, the SmartLink and pre-save state, the theme, the countdown ("9 days to go", "117 days out")
 * and the two actions: "Download all (.zip)" (secondary) and "Open Studio" (primary, with the song
 * picked). The song title is the page title above it.
 */
const ReleaseHeaderComponent: React.FC<ReleaseHeaderProps> = (props) => {
  const { accountId, item, themeId, hasReadyAsset, zip, onOpenStudio } = props;
  const intl = useIntl();
  const router = useSMLRouter();
  const days = useMemo(() => daysUntilRelease(item.releaseDate, new Date()), [item.releaseDate]);
  const address = useSmartLinkAddress(accountId, item.smartLinkSlug);
  const eyebrow = useMemo(
    () =>
      [
        item.releaseType,
        item.kind === CatalogItemKind.unreleased ? intl.formatMessage(releaseMessages.unreleased) : null,
      ]
        .filter((part): part is string => part !== null && part !== '')
        .join(' · '),
    [intl, item.kind, item.releaseType]
  );

  const { smartLinkId } = item;
  const openSmartLink = useCallback(() => {
    if (smartLinkId !== null) {
      router.push(SMLRoutes.manage, { accountId, smartLinkId });
    }
  }, [accountId, router, smartLinkId]);
  const theme = intl.formatMessage(releaseThemeMessages[themeId ?? 'clean'].name);
  const isUpcoming = days !== null && days >= 0;
  const { canUsePresave } = useMusic24Capabilities();
  const isDemo = useIsDemoAccount();
  // Free has no pre-save page: an unreleased song says so, with the way to a plan that has it.
  const showPresavePlanNote = showsPresavePlanNote({
    canUsePresave,
    isDemo,
    kind: item.kind,
    presaveCampaignUuid: item.presaveCampaignUuid,
  });

  return (
    <div className={styles.head}>
      <ReleaseArtwork item={item} className={styles.art} sizePx={160} />
      <div className={styles.meta}>
        {eyebrow !== '' && <span className={styles.eyebrow}>{eyebrow}</span>}
        <span className={styles.by}>{item.artistName}</span>
        <div className={styles.chips}>
          <span className={styles.chip}>{formatReleaseWhen(intl, item.releaseDate, days)}</span>
          {/* The row says if the song has a SmartLink. The address is null while the account loads,
              so it cannot decide the chip: the chip said "No SmartLink" for a moment (QA mob-12). */}
          {item.smartLinkId === null || item.smartLinkSlug === null ? (
            <span className={styles.chip}>{intl.formatMessage(releaseMessages.noSmartLink)}</span>
          ) : (
            <button type="button" className={styles.chip} onClick={openSmartLink} title={address ?? undefined}>
              {intl.formatMessage(releaseMessages.smartLinkLive)}
            </button>
          )}
          {item.presaveCampaignUuid !== null && (
            <span className={styles.chip}>
              {intl.formatMessage(isUpcoming ? releaseMessages.presaveLive : releaseMessages.presaveEnded)}
            </span>
          )}
          <span className={styles.chip}>{intl.formatMessage(releaseMessages.theme, { theme })}</span>
        </div>
        {showPresavePlanNote && <ReleasePresavePlanNote />}
      </div>
      <div className={styles.side}>
        {days !== null && days !== 0 && (
          <div className={styles.countdown}>
            <span className={styles.countdownNumber}>{Math.abs(days)}</span>
            <span className={styles.countdownUnit}>
              {intl.formatMessage(days > 0 ? releaseMessages.daysToGo : releaseMessages.daysOut, {
                days: Math.abs(days),
              })}
            </span>
          </div>
        )}
        <div className={styles.actions}>
          {hasReadyAsset && zip.readyUrl === null && (
            <SMLButton disabled={zip.isZipping} onClick={zip.download} icon={<FontAwesomeIcon icon={faDownload} />}>
              {intl.formatMessage(releaseMessages.downloadAll)}
            </SMLButton>
          )}
          {zip.readyUrl !== null && (
            <SMLButton href={zip.readyUrl} icon={<FontAwesomeIcon icon={faDownload} />}>
              {intl.formatMessage(releaseMessages.downloadZip)}
            </SMLButton>
          )}
          <SMLButton variant="primary" onClick={onOpenStudio} icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}>
            {intl.formatMessage(releaseMessages.openStudio)}
          </SMLButton>
        </div>
      </div>
    </div>
  );
};

export const ReleaseHeader = TypedMemo(ReleaseHeaderComponent);
